import { useEffect, useId, useState } from "react";

import { AlertTriangle, XCircle } from "lucide-react";
import { useDispatch } from "react-redux";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  useBundleSplitterUpload,
  useClassifyFiles,
} from "@/hooks/mutations/use-bundle-splitter";
import { getFriendlyErrorMessage, notifyError } from "@/utils/notification";

import { FilePreview } from "../FilePreview";
import type { BundleRowStatus } from "../store/useBundleUploads";
import { extractTaskIds } from "../utils";
import type { UploadConflict } from "../utils/uploadConflicts";

// MDRS-550: a file picked twice waits for the user to choose.
const DUPLICATE_MESSAGES: Record<
  NonNullable<UploadConflict["duplicate"]>,
  string
> = {
  list: "This file is already in the list for this case.",
  earlier: "A file with the same name and size was already uploaded to this case.",
};

type UploadPhaseProps = {
  file: File;
  userId: string;
  caseNumber: string;
  onTaskCreated: (taskId: string) => void;
  onDismiss?: () => void;
  onBusyChange?: (busy: boolean) => void;
  onStatusChange?: (status: BundleRowStatus) => void;
  /** MDRS-550: picked twice — show Upload anyway / Remove instead of Continue. */
  duplicate?: UploadConflict["duplicate"];
  /**
   * MDRS-550: a same-name file for the case is still on its way to the
   * splitter; starting would overwrite it in storage.
   */
  isStartBlocked?: boolean;
};

export const UploadPhase = ({
  file,
  userId,
  caseNumber,
  onTaskCreated,
  onDismiss,
  onBusyChange,
  onStatusChange,
  duplicate,
  isStartBlocked = false,
}: UploadPhaseProps) => {
  const blockedNoteId = useId();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isClassifying, setIsClassifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dispatch = useDispatch();

  const bundleSplitterUpload = useBundleSplitterUpload();
  const {
    mutateAsync: classifyFiles,
    isPending: isClassifyPending,
    isSuccess: isClassifySuccess,
    isError: isClassifyError,
  } = useClassifyFiles();

  const handleClassify = async () => {
    try {
      setIsUploading(true);
      setErrorMessage(null);
      setUploadProgress(0);

      const filePath = await bundleSplitterUpload.uploadFile(
        file,
        caseNumber,
        (progress) => setUploadProgress(progress)
      );
      setIsUploading(false);
      setIsClassifying(true);

      const requestData = {
        file_paths: [filePath],
        user_id: userId,
        case_number: caseNumber,
      };

      const response = await classifyFiles(requestData);
      const extractedTaskIds = extractTaskIds(response);

      if (extractedTaskIds.length > 0) {
        onTaskCreated(extractedTaskIds[0]);
      } else {
        throw new Error("No task ID returned from classification");
      }
    } catch (err) {
      console.error("Error processing file:", err);
      notifyError(err, dispatch);
      setErrorMessage(getFriendlyErrorMessage(err));
      setIsUploading(false);
      setIsClassifying(false);
      setUploadProgress(0);
    }
  };

  const isPending =
    isUploading || bundleSplitterUpload.isLoading || isClassifyPending;
  const hasError = !!(bundleSplitterUpload.error || isClassifyError);

  // MDRS-550: a refresh or close here loses the upload, so flag it until the
  // task exists (this phase then unmounts) or the attempt fails.
  const isBusy = isPending || isClassifying;
  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  // MDRS-550: the pop-up's per-stage counts.
  const status: BundleRowStatus = isUploading
    ? "uploading"
    : isClassifying || isClassifyPending
      ? "preparing"
      : errorMessage || hasError
        ? "failed"
        : "ready";
  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  return (
    <>
      {errorMessage && (
        <Alert variant="destructive" className="mb-3">
          <XCircle />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <FilePreview
        file={file}
        isSuccess={isClassifySuccess}
        isFailed={hasError}
        isLoading={isPending}
        uploadProgress={isUploading ? uploadProgress : undefined}
        onClear={!isPending ? onDismiss : undefined}
      />

      {isClassifying && (
        <div className="space-y-1.5">
          <Progress className="h-2 animate-pulse" />
          <p className="text-center text-xs text-foreground-muted">
            Preparing Classification
          </p>
        </div>
      )}

      {isUploading ? (
        <p className="text-center text-xs text-foreground-muted">
          {uploadProgress > 0
            ? `Uploading File (${Math.round(uploadProgress)}%)`
            : "Uploading File"}
        </p>
      ) : (
        !isClassifyPending &&
        !isClassifying && (
          // A flex column, like the row card: a lone button stretches to
          // the row's width, as it did before this wrapper existed.
          <div className="flex flex-col gap-2">
            {duplicate && (
              <Alert variant="warning">
                <AlertTriangle className="mt-0.5 text-warning" />
                <AlertDescription>{DUPLICATE_MESSAGES[duplicate]}</AlertDescription>
              </Alert>
            )}
            {isStartBlocked && (
              <p id={blockedNoteId} className="text-xs text-foreground-muted">
                A file with this name is still uploading or splitting for this case.
                Start this one once it finishes — starting now would overwrite it.
              </p>
            )}
            {duplicate ? (
              <div className="flex gap-2">
                {onDismiss && (
                  <Button
                    variant="simple-outline"
                    className="flex-1"
                    onClick={onDismiss}
                  >
                    Remove
                  </Button>
                )}
                <Button
                  className="flex-1"
                  onClick={handleClassify}
                  isLoading={isPending}
                  disabled={isPending || isStartBlocked}
                  aria-describedby={isStartBlocked ? blockedNoteId : undefined}
                >
                  Upload anyway
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleClassify}
                isLoading={isPending}
                disabled={isPending || isStartBlocked}
                aria-describedby={isStartBlocked ? blockedNoteId : undefined}
              >
                Continue to Processing
              </Button>
            )}
          </div>
        )
      )}
    </>
  );
};
